import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import Swal from "sweetalert2";

import formatCurrency from "../shared/formatCurrency";
import {
  approveNzengoTransfer,
  getActiveNzengoMemberships,
  getPendingNzengoTransferRequests,
  getTransferredNzengoMemberships,
  rejectNzengoTransfer,
} from "../services/nzengoTransferService";

const brand = "#800b33";
const brandHover = "#6c0a2b";
const currency = (amount) => `TZS ${formatCurrency(amount, "en-TZ")}`;

export default function NzengoTransferApprovals() {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [membershipSummary, setMembershipSummary] = useState({ active: 0, transferred: 0 });

  const refresh = () => {
    setRequests(getPendingNzengoTransferRequests());
    setMembershipSummary({
      active: getActiveNzengoMemberships().length,
      transferred: getTransferredNzengoMemberships().length,
    });
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleApprove = (request) => {
    Swal.fire({
      icon: "question",
      title: "Approve transfer?",
      text: `${request.member?.fullName} will become active in ${request.destinationGroup?.name}.`,
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: brand,
    }).then((result) => {
      if (!result.isConfirmed) return;
      approveNzengoTransfer(request.id);
      refresh();
      setSelectedRequest(null);
      Swal.fire({
        icon: "success",
        title: "Transfer Approved",
        text: "Source membership is now historical and destination membership is active.",
        confirmButtonColor: brand,
      });
    });
  };

  const handleReject = (request) => {
    Swal.fire({
      icon: "warning",
      title: "Reject transfer?",
      text: `${request.member?.fullName} will remain active in ${request.sourceGroup?.name}.`,
      showCancelButton: true,
      confirmButtonText: "Reject",
      confirmButtonColor: brand,
    }).then((result) => {
      if (!result.isConfirmed) return;
      rejectNzengoTransfer(request.id);
      refresh();
      setSelectedRequest(null);
      Swal.fire({
        icon: "info",
        title: "Transfer Rejected",
        text: "The member remains active in the source Nzengo.",
        confirmButtonColor: brand,
      });
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            Pending Nzengo Transfer Requests
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Destination chairperson approval queue for member transfers.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip icon={<PeopleAltIcon />} label={`${membershipSummary.active} active mock memberships`} />
          <Chip label={`${membershipSummary.transferred} transferred history`} variant="outlined" />
        </Stack>
      </Stack>

      {requests.length === 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          No pending transfer requests. Approved transfers keep source memberships as transferred history.
        </Alert>
      )}

      <Grid container spacing={2}>
        {requests.map((request) => (
          <Grid item xs={12} md={6} key={request.id}>
            <Card sx={{ borderRadius: 3, boxShadow: 2, height: "100%" }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      {request.member?.fullName}
                    </Typography>
                    <Chip color="warning" size="small" label="Pending Approval" />
                  </Stack>
                  <Divider />
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">
                        Source Nzengo
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }}>{request.sourceGroup?.name}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">
                        Destination Nzengo
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }}>{request.destinationGroup?.name}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">
                        Location
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }}>
                        {request.destinationGroup?.district}, {request.destinationGroup?.region}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">
                        Outstanding Obligations
                      </Typography>
                      <Typography sx={{ fontWeight: 900, color: "success.main" }}>
                        {currency(request.outstandingAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Requested Date
                      </Typography>
                      <Typography sx={{ fontWeight: 800 }}>
                        {new Date(request.requestedAt).toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="outlined" onClick={() => setSelectedRequest(request)}>
                      View Request
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleApprove(request)}
                      sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => handleReject(request)}
                    >
                      Reject
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(selectedRequest)} onClose={() => setSelectedRequest(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Request Details</DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Stack spacing={2}>
              <Alert severity="info">
                Approval activates the destination membership and preserves the source membership as transferred history.
              </Alert>
              {[
                ["Member", selectedRequest.member?.fullName],
                ["Source Nzengo", selectedRequest.sourceGroup?.name],
                ["Destination Nzengo", selectedRequest.destinationGroup?.name],
                ["Location", `${selectedRequest.destinationGroup?.district}, ${selectedRequest.destinationGroup?.region}`],
                ["Eligibility", selectedRequest.outstandingAmount === 0 ? "Obligations cleared" : "Blocked"],
                ["Outstanding Balance", currency(selectedRequest.outstandingAmount)],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography sx={{ fontWeight: 800 }}>{value}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRequest(null)}>Close</Button>
          {selectedRequest && (
            <>
              <Button color="error" onClick={() => handleReject(selectedRequest)}>
                Reject
              </Button>
              <Button
                variant="contained"
                onClick={() => handleApprove(selectedRequest)}
                sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
